import { useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Building2, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const AdminCompanies = () => {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [showForm, setShowForm] = useState(false);

  const { data: companies = [], isLoading } = useQuery({
    queryKey: ["admin-companies"],
    queryFn: async () => {
      const { data } = await supabase.from("companies").select("*").order("name");
      return data ?? [];
    },
  });

  const addCompany = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("companies").insert({ name: name.trim() });
      if (error) throw error;
    },
    onSuccess: () => {
      setName("");
      setShowForm(false);
      qc.invalidateQueries({ queryKey: ["admin-companies"] });
      qc.invalidateQueries({ queryKey: ["admin-company-count"] });
      toast.success("Company added");
    },
    onError: () => toast.error("Failed to add company"),
  });

  const deleteCompany = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("companies").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-companies"] });
      qc.invalidateQueries({ queryKey: ["admin-company-count"] });
      toast.success("Company deleted");
    },
    onError: () => toast.error("Failed to delete company"),
  });

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-teal/60 text-[11px] font-bold tracking-[4px] uppercase font-body mb-2">Admin</p>
          <h1 className="font-display text-3xl text-primary-foreground tracking-tight">
            Client <span className="accent-text">Companies</span>
          </h1>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal to-teal-light text-navy-deep text-[12px] font-bold tracking-[1.5px] uppercase rounded-xl hover:shadow-lg hover:shadow-teal/25 transition-all font-body"
        >
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? "Cancel" : "Add Company"}
        </button>
      </div>

      {showForm && (
        <div className="glass-card rounded-2xl p-6 mb-6">
          <h2 className="font-display text-lg text-primary-foreground mb-4">New Company</h2>
          <form
            onSubmit={(e) => { e.preventDefault(); if (name.trim()) addCompany.mutate(); }}
            className="flex gap-3"
          >
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Company name"
              required
              className="flex-1 px-4 py-3 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 transition-all"
            />
            <button
              type="submit"
              disabled={addCompany.isPending}
              className="px-6 py-3 bg-gradient-to-r from-teal to-teal-light text-navy-deep text-[12px] font-bold tracking-[1.5px] uppercase rounded-xl hover:shadow-lg hover:shadow-teal/25 transition-all font-body disabled:opacity-50"
            >
              Create
            </button>
          </form>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
        </div>
      ) : companies.length === 0 ? (
        <div className="glass-card rounded-2xl p-16 text-center">
          <Building2 className="w-12 h-12 text-primary-foreground/15 mx-auto mb-4" />
          <p className="text-primary-foreground/40 font-body">No companies yet. Add your first one.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {companies.map((c) => (
            <div key={c.id} className="glass-card rounded-xl p-5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-teal/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-teal" />
                </div>
                <div>
                  <p className="text-sm text-primary-foreground/80 font-body font-medium">{c.name}</p>
                  <p className="text-xs text-primary-foreground/25 font-body">{c.id}</p>
                </div>
              </div>
              <button
                onClick={() => { if (confirm(`Delete "${c.name}"?`)) deleteCompany.mutate(c.id); }}
                className="p-2 rounded-lg text-primary-foreground/20 hover:text-destructive hover:bg-destructive/5 transition-all"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminCompanies;
